import { MOCK_NOTICE } from './constants';

const CORE_APR = 8.4;

function withTenantDerivedFields(tenant) {
  const tvl = tenant.tvl ?? tenant.treasuryTvl ?? 0;
  const aprDeltaVsCore = Number((Number(tenant.apr ?? 0) - CORE_APR).toFixed(2));
  const reasonSeverity =
    tenant.governanceStatus === 'sanctioned'
      ? 'constitutional'
      : tenant.governanceStatus === 'restricted' || tenant.governanceStatus === 'under-review'
        ? 'warning'
        : tenant.treasuryHealth === 'review-required'
          ? 'warning'
          : 'info';
  const reasonCode =
    tenant.governanceStatus === 'sanctioned'
      ? 'LOCAL_GOVERNANCE_MODEL_INCOMPATIBLE'
      : tenant.governanceStatus === 'restricted'
        ? 'REMOTE_EXECUTION_GUARDRAIL_ACTIVE'
        : tenant.treasuryHealth === 'review-required'
          ? 'TREASURY_POLICY_REQUIRES_REVIEW'
          : 'VOTING_POWER_SOURCE_NOT_VERIFIED';

  return {
    coreApr: CORE_APR,
    aprDeltaVsCore,
    treasuryTvl: tvl,
    tvl,
    route: `/governance/dao/${tenant.id}`,
    constitutionalStanding: tenant.constitutionalStanding ?? tenant.governanceStatus,
    supportedProducts: tenant.supportedProducts ?? tenant.productsEnabled ?? [],
    governanceModel: tenant.governanceModel ?? `${tenant.category} local governance`,
    localGovernanceModel: tenant.localGovernanceModel ?? 'token-voting + treasury policy review',
    proposalQueue: tenant.proposalQueue ?? [
      {
        id: `${tenant.id}-proposal-001`,
        title: `${tenant.symbol} treasury policy update`,
        status: tenant.pendingOperations > 0 ? 'open' : 'queued',
        category: 'Treasury',
        executionChain: tenant.chains?.[0] ?? 'Ethereum Sepolia',
        treasuryImpact: tenant.pendingOperations > 2 ? 'review-required' : 'bounded',
        riskSeverity: reasonSeverity === 'constitutional' ? 'constitutional' : reasonSeverity,
      },
      {
        id: `${tenant.id}-proposal-002`,
        title: `${tenant.symbol} product access renewal`,
        status: tenant.activeProposals > 1 ? 'voting' : 'draft',
        category: 'Products',
        executionChain: tenant.chains?.[1] ?? tenant.chains?.[0] ?? 'Base',
        treasuryImpact: 'low',
        riskSeverity: tenant.riskLevel === 'experimental' || tenant.riskLevel === 'high' ? 'warning' : 'info',
      },
    ],
    operationsQueue: tenant.operationsQueue ?? [
      {
        id: `${tenant.id}-operation-001`,
        label: 'Treasury execution review',
        status: tenant.treasuryHealth === 'active' ? 'ready' : 'review-required',
        chain: tenant.chains?.[0] ?? 'Ethereum Sepolia',
        source: 'treasury policy',
        reasonCode: tenant.treasuryHealth === 'active' ? null : 'TREASURY_POLICY_REQUIRES_REVIEW',
        reasonSeverity: tenant.treasuryHealth === 'active' ? 'info' : 'warning',
      },
      {
        id: `${tenant.id}-operation-002`,
        label: 'Plugin capability sync',
        status: tenant.governanceStatus === 'compliant' ? 'observed' : 'guarded',
        chain: tenant.chains?.[1] ?? tenant.chains?.[0] ?? 'Base',
        source: 'plugin capability',
        reasonCode: tenant.governanceStatus === 'compliant' ? null : 'PLUGIN_CAPABILITY_NOT_REGISTERED',
        reasonSeverity: tenant.governanceStatus === 'compliant' ? 'info' : reasonSeverity,
      },
    ],
    executionReceiptFeed: tenant.executionReceiptFeed ?? [
      {
        id: `${tenant.id}-receipt-001`,
        label: 'Governance state indexed',
        status: 'indexed',
        chain: tenant.chains?.[0] ?? 'Ethereum Sepolia',
        source: 'governance indexer',
      },
      {
        id: `${tenant.id}-receipt-002`,
        label: 'Tenant registry checkpoint',
        status: tenant.executionReceipts > 2 ? 'reconciled' : 'pending',
        chain: tenant.chains?.[1] ?? tenant.chains?.[0] ?? 'Base',
        source: 'federation registry',
      },
    ],
    reasonCodes: tenant.reasonCodes ?? [
      {
        reasonCode,
        reasonSeverity,
        source: tenant.treasuryHealth === 'review-required' ? 'treasury policy' : 'federation registry',
        scope: tenant.name,
      },
    ],
    ...tenant,
    aprDeltaVsCore,
    treasuryTvl: tvl,
    tvl,
    route: `/governance/dao/${tenant.id}`,
  };
}

const tenants = [
  {
    id: 'dao-tenant-trading-alpha',
    daoId: 'dao-trading-alpha-001',
    name: 'Axodus Trading Alpha DAO',
    symbol: 'ATAD',
    category: 'Trading DAO',
    investmentTypes: ['Trading', 'DeFi', 'Treasury'],
    description: 'Market-neutral and directional strategy tenant focused on governed execution, risk limits and treasury reporting.',
    tvl: 12800000,
    members: 4820,
    governanceStatus: 'compliant',
    federationTier: 'partner',
    apr: 13.22,
    riskLevel: 'balanced',
    featured: true,
    featuredOrder: 1,
    productsEnabled: ['Trading Console', 'Treasury Vault', 'Risk Reports'],
    chains: ['Ethereum Sepolia', 'Base', 'Arbitrum'],
    performanceTrend: '+3.8% 30d',
    governanceMaturity: 'operational',
    treasuryHealth: 'active',
    strategy: 'Governed trading allocation across delta-neutral, stablecoin and tactical ETH exposure with execution limits controlled by tenant proposals.',
    riskMetrics: { score: 'Balanced', liquidity: 'High', concentration: 'Moderate', execution: 'Guarded' },
    agentsAssigned: ['trade-risk-agent', 'treasury-report-agent'],
    activeProposals: 3,
    pendingOperations: 2,
    executionReceipts: 8,
    treasuryAllocation: [
      { label: 'Stablecoin reserve', value: 42 },
      { label: 'ETH tactical exposure', value: 28 },
      { label: 'Market neutral routes', value: 20 },
      { label: 'Operations buffer', value: 10 },
    ],
    historicalPerformance: [
      { period: '7d', apr: 12.4, pnl: '+1.2%' },
      { period: '30d', apr: 13.22, pnl: '+3.8%' },
      { period: '90d', apr: 11.9, pnl: '+8.1%' },
    ],
  },
  {
    id: 'dao-tenant-mining-yield',
    daoId: 'dao-mining-yield-001',
    name: 'Axodus Mining Yield DAO',
    symbol: 'AMYD',
    category: 'Mining DAO',
    investmentTypes: ['Mining', 'Yield', 'Treasury'],
    description: 'Infrastructure-backed tenant DAO coordinating mining allocation, reward accounting and treasury exposure.',
    tvl: 9400000,
    members: 3160,
    governanceStatus: 'compliant',
    federationTier: 'partner',
    apr: 10.15,
    riskLevel: 'moderate',
    featured: true,
    featuredOrder: 2,
    productsEnabled: ['Mining Pools', 'Treasury Reports', 'Marketplace Access'],
    chains: ['Polygon', 'Base'],
    performanceTrend: '+1.4% 30d',
    governanceMaturity: 'review-ready',
    treasuryHealth: 'active',
    strategy: 'Mining allocation tenant coordinating provider exposure, rewards accounting, equipment policy and treasury attribution.',
    riskMetrics: { score: 'Moderate', liquidity: 'Moderate', concentration: 'Moderate', execution: 'Queued' },
    agentsAssigned: ['mining-provider-agent', 'reward-accounting-agent'],
    activeProposals: 2,
    pendingOperations: 4,
    executionReceipts: 5,
    treasuryAllocation: [
      { label: 'Provider allocation', value: 48 },
      { label: 'Reward reserve', value: 24 },
      { label: 'Equipment policy', value: 18 },
      { label: 'Operations buffer', value: 10 },
    ],
    historicalPerformance: [
      { period: '7d', apr: 9.8, pnl: '+0.4%' },
      { period: '30d', apr: 10.15, pnl: '+1.4%' },
      { period: '90d', apr: 9.6, pnl: '+4.9%' },
    ],
  },
  {
    id: 'dao-tenant-ai-infra',
    daoId: 'dao-ai-infra-001',
    name: 'Axodus AI Infrastructure DAO',
    symbol: 'AAID',
    category: 'AI Infrastructure DAO',
    investmentTypes: ['AI Infrastructure', 'Compute', 'Venture'],
    description: 'Compute, agent orchestration and model infrastructure tenant with governed operational budgets.',
    tvl: 7600000,
    members: 2210,
    governanceStatus: 'under-review',
    federationTier: 'sovereign',
    apr: 16.88,
    riskLevel: 'high',
    featured: true,
    featuredOrder: 3,
    productsEnabled: ['MCP Services', 'Compute Budgeting', 'ACS Agent Console'],
    chains: ['Ethereum Sepolia', 'Arbitrum'],
    performanceTrend: '+6.1% 30d',
    governanceMaturity: 'under-review',
    treasuryHealth: 'review-required',
    strategy: 'AI infrastructure tenant for governed compute budgets, agent orchestration, MCP services and operational policy experiments.',
    riskMetrics: { score: 'High', liquidity: 'Moderate', concentration: 'High', execution: 'Review required' },
    agentsAssigned: ['agent-policy-monitor', 'compute-budget-agent'],
    activeProposals: 5,
    pendingOperations: 3,
    executionReceipts: 4,
    treasuryAllocation: [
      { label: 'Compute reserve', value: 38 },
      { label: 'Agent operations', value: 26 },
      { label: 'MCP services', value: 22 },
      { label: 'Review buffer', value: 14 },
    ],
    historicalPerformance: [
      { period: '7d', apr: 15.7, pnl: '+2.7%' },
      { period: '30d', apr: 16.88, pnl: '+6.1%' },
      { period: '90d', apr: 14.2, pnl: '+9.8%' },
    ],
  },
  {
    id: 'dao-tenant-education',
    daoId: 'dao-education-001',
    name: 'Axodus Academy DAO',
    symbol: 'AEDA',
    category: 'Educational DAO',
    investmentTypes: ['Education', 'Marketplace'],
    description: 'Education, proof-of-knowledge and certification tenant with lower volatility and community governance.',
    tvl: 4200000,
    members: 9080,
    governanceStatus: 'compliant',
    federationTier: 'observer',
    apr: 6.29,
    riskLevel: 'low',
    featured: false,
    featuredOrder: null,
    productsEnabled: ['Academy Paths', 'Certification', 'Marketplace Licenses'],
    chains: ['Polygon', 'Harmony Mainnet'],
    performanceTrend: '-0.6% 30d',
    governanceMaturity: 'community',
    treasuryHealth: 'guarded',
    strategy: 'Education tenant for constitutional literacy, proof-of-knowledge, certification and community governance onboarding.',
    riskMetrics: { score: 'Low', liquidity: 'High', concentration: 'Low', execution: 'Guarded' },
    agentsAssigned: ['curriculum-review-agent', 'credential-monitor-agent'],
    activeProposals: 1,
    pendingOperations: 1,
    executionReceipts: 3,
    treasuryAllocation: [
      { label: 'Education rewards', value: 36 },
      { label: 'Certification operations', value: 24 },
      { label: 'Marketplace enablement', value: 18 },
      { label: 'Community reserve', value: 22 },
    ],
    historicalPerformance: [
      { period: '7d', apr: 6.4, pnl: '+0.1%' },
      { period: '30d', apr: 6.29, pnl: '-0.6%' },
      { period: '90d', apr: 6.8, pnl: '+2.2%' },
    ],
  },
  {
    id: 'dao-tenant-defi-stability',
    daoId: 'dao-defi-stability-001',
    name: 'Axodus Defi Stability DAO',
    symbol: 'ADSD',
    category: 'DeFi DAO',
    investmentTypes: ['DeFi', 'Staking', 'Yield'],
    description: 'Lower-volatility DeFi tenant focused on staking, conservative yield routes and treasury-grade reporting.',
    tvl: 15800000,
    members: 6110,
    governanceStatus: 'compliant',
    federationTier: 'partner',
    apr: 8.4,
    riskLevel: 'low',
    featured: false,
    featuredOrder: null,
    productsEnabled: ['Staking', 'Vault', 'Treasury Reports'],
    chains: ['Ethereum Sepolia', 'Base'],
    performanceTrend: '+0.2% 30d',
    governanceMaturity: 'operational',
    treasuryHealth: 'active',
    strategy: 'Conservative DeFi allocation designed to track the CORE baseline while exposing local DAO governance controls.',
    riskMetrics: { score: 'Low', liquidity: 'High', concentration: 'Low', execution: 'Guarded' },
    agentsAssigned: ['yield-policy-agent'],
    activeProposals: 2,
    pendingOperations: 1,
    executionReceipts: 6,
    treasuryAllocation: [
      { label: 'Staking reserve', value: 40 },
      { label: 'Stable yield routes', value: 32 },
      { label: 'Liquidity buffer', value: 18 },
      { label: 'Operations', value: 10 },
    ],
    historicalPerformance: [
      { period: '7d', apr: 8.35, pnl: '+0.1%' },
      { period: '30d', apr: 8.4, pnl: '+0.2%' },
      { period: '90d', apr: 8.2, pnl: '+1.8%' },
    ],
  },
  {
    id: 'dao-tenant-marketplace-growth',
    daoId: 'dao-marketplace-growth-001',
    name: 'Axodus Marketplace Growth DAO',
    symbol: 'AMGD',
    category: 'Marketplace DAO',
    investmentTypes: ['Marketplace', 'Venture', 'Treasury'],
    description: 'Commerce tenant coordinating seller growth, product validation budgets and marketplace treasury incentives.',
    tvl: 3100000,
    members: 1840,
    governanceStatus: 'restricted',
    federationTier: 'restricted',
    apr: 5.95,
    riskLevel: 'experimental',
    featured: false,
    featuredOrder: null,
    productsEnabled: ['Marketplace', 'Licenses', 'Seller Operations'],
    chains: ['Polygon', 'Base'],
    performanceTrend: '-1.1% 30d',
    governanceMaturity: 'restricted',
    treasuryHealth: 'restricted',
    strategy: 'Marketplace incentive tenant under restricted standing while product validation and seller controls mature.',
    riskMetrics: { score: 'Experimental', liquidity: 'Low', concentration: 'High', execution: 'Restricted' },
    agentsAssigned: ['seller-risk-agent'],
    activeProposals: 4,
    pendingOperations: 5,
    executionReceipts: 2,
    treasuryAllocation: [
      { label: 'Seller incentives', value: 34 },
      { label: 'Product validation', value: 26 },
      { label: 'Marketplace liquidity', value: 22 },
      { label: 'Restricted reserve', value: 18 },
    ],
    historicalPerformance: [
      { period: '7d', apr: 5.7, pnl: '-0.3%' },
      { period: '30d', apr: 5.95, pnl: '-1.1%' },
      { period: '90d', apr: 6.5, pnl: '+0.7%' },
    ],
  },
  {
    id: 'dao-tenant-venture-frontier',
    daoId: 'dao-venture-frontier-001',
    name: 'Axodus Venture Frontier DAO',
    symbol: 'AVFD',
    category: 'Venture DAO',
    investmentTypes: ['Venture', 'AI Infrastructure', 'Compute'],
    description: 'Frontier venture tenant for early-stage ecosystem bets, compute experiments and long-horizon treasury allocation.',
    tvl: 2200000,
    members: 980,
    governanceStatus: 'sanctioned',
    federationTier: 'observer',
    apr: 3.1,
    riskLevel: 'experimental',
    featured: false,
    featuredOrder: null,
    productsEnabled: ['Venture Review', 'Compute Diligence'],
    chains: ['Arbitrum'],
    performanceTrend: '-4.2% 30d',
    governanceMaturity: 'sanctioned',
    treasuryHealth: 'restricted',
    strategy: 'Experimental venture tenant intentionally constrained by constitutional standing and treasury review boundaries.',
    riskMetrics: { score: 'Experimental', liquidity: 'Low', concentration: 'High', execution: 'Sanctioned' },
    agentsAssigned: ['diligence-monitor-agent'],
    activeProposals: 1,
    pendingOperations: 6,
    executionReceipts: 1,
    treasuryAllocation: [
      { label: 'Venture reserve', value: 44 },
      { label: 'Compute diligence', value: 24 },
      { label: 'Strategic options', value: 20 },
      { label: 'Sanction buffer', value: 12 },
    ],
    historicalPerformance: [
      { period: '7d', apr: 3.8, pnl: '-0.9%' },
      { period: '30d', apr: 3.1, pnl: '-4.2%' },
      { period: '90d', apr: 4.4, pnl: '-7.6%' },
    ],
  },
  {
    id: 'dao-tenant-treasury-core-plus',
    daoId: 'dao-treasury-core-plus-001',
    name: 'Axodus Treasury Core Plus DAO',
    symbol: 'ATCP',
    category: 'Treasury DAO',
    investmentTypes: ['Treasury', 'Staking', 'Yield'],
    description: 'Treasury-oriented tenant for conservative users who want a slight extension beyond CORE without high strategy drift.',
    tvl: 7900000,
    members: 5440,
    governanceStatus: 'compliant',
    federationTier: 'sovereign',
    apr: 9.05,
    riskLevel: 'moderate',
    featured: false,
    featuredOrder: null,
    productsEnabled: ['Treasury Vault', 'Staking', 'Risk Reports'],
    chains: ['Ethereum Sepolia', 'Polygon'],
    performanceTrend: '+0.8% 30d',
    governanceMaturity: 'operational',
    treasuryHealth: 'active',
    strategy: 'CORE-adjacent treasury allocation for users seeking modest yield extension with strong observability.',
    riskMetrics: { score: 'Moderate', liquidity: 'High', concentration: 'Low', execution: 'Guarded' },
    agentsAssigned: ['treasury-policy-agent'],
    activeProposals: 2,
    pendingOperations: 2,
    executionReceipts: 7,
    treasuryAllocation: [
      { label: 'CORE reserve', value: 46 },
      { label: 'Staking routes', value: 28 },
      { label: 'Yield buffer', value: 16 },
      { label: 'Operations', value: 10 },
    ],
    historicalPerformance: [
      { period: '7d', apr: 8.9, pnl: '+0.2%' },
      { period: '30d', apr: 9.05, pnl: '+0.8%' },
      { period: '90d', apr: 8.7, pnl: '+2.6%' },
    ],
  },
].map(withTenantDerivedFields);

const tenantTreasuryTvl = tenants.reduce((total, tenant) => total + tenant.tvl, 0);
const totalMembers = tenants.reduce((total, tenant) => total + tenant.members, 0);
const averageTenantApr = tenants.reduce((total, tenant) => total + tenant.apr, 0) / tenants.length;

export const governanceTenantsMock = {
  mockNotice: MOCK_NOTICE,
  coreMetrics: {
    name: 'Axodus Federal DAO',
    symbol: 'CORE',
    apr: CORE_APR,
    treasuryTvl: 18400000,
    members: 12480,
    governanceStatus: 'compliant',
    federationTier: 'root',
    riskLevel: 'core',
    treasuryHealth: 'review-required',
    description: 'Federal treasury baseline for users who prefer central Axodus allocation without tenant DAO exposure.',
  },
  federationStats: {
    tenantCount: tenants.length,
    totalTreasuryTvl: tenantTreasuryTvl,
    averageTenantApr,
    activeMembers: totalMembers,
    executionQueues: 9,
  },
  tenants,
  userAllocation: {
    userId: 'mock-user-001',
    coreAllocation: 14000,
    tenantAllocations: [
      { tenantId: 'dao-tenant-trading-alpha', allocatedCapital: 5200, sharePercent: 0.041, rewards: 188.42, pnl: 412.9, votingPower: '12,840 vNEURONS' },
      { tenantId: 'dao-tenant-mining-yield', allocatedCapital: 2100, sharePercent: 0.022, rewards: 61.18, pnl: 94.2, votingPower: '4,120 vNEURONS' },
      { tenantId: 'dao-tenant-treasury-core-plus', allocatedCapital: 3200, sharePercent: 0.034, rewards: 48.72, pnl: 76.4, votingPower: '6,430 vNEURONS' },
    ],
  },
  coreOnlyUserAllocation: {
    userId: 'mock-user-core-only',
    coreAllocation: 25000,
    tenantAllocations: [],
  },
  userAllocations: [
    {
      tenantId: 'dao-tenant-trading-alpha',
      userPosition: 'active',
      allocatedCapital: 5200,
      daoSharePercent: 0.041,
      rewards: 188.42,
      pnl: 412.9,
      governanceRights: 'vote + proposal review',
      votingPower: '12,840 vNEURONS',
      treasuryExposure: 'Trading, stablecoin, ETH delta',
      recentDaoActivity: 'Treasury rebalance proposal opened',
    },
    {
      tenantId: 'dao-tenant-mining-yield',
      userPosition: 'monitoring',
      allocatedCapital: 2100,
      daoSharePercent: 0.022,
      rewards: 61.18,
      pnl: 94.2,
      governanceRights: 'vote',
      votingPower: '4,120 vNEURONS',
      treasuryExposure: 'Mining rewards and equipment allocation',
      recentDaoActivity: 'Reward accounting report published',
    },
  ],
  tenantPerformance: {
    baseline: 'Axodus CORE APR',
    comparisonWindow: '30d',
  },
  treasuryMetrics: {
    totalTreasuryTvl: tenantTreasuryTvl + 18400000,
    tenantTreasuryTvl,
    coreTreasuryTvl: 18400000,
  },
  governanceMetrics: {
    activeProposals: 14,
    openProposals: 14,
    recentlyFinalizedProposals: 6,
    pendingExecutions: 9,
    constitutionalAlerts: 3,
    governanceHealth: 'operational',
  },
};
